import { useEffect, useState } from "react";
import { makeStyles } from "tss-react/mui";
import { MiAvatar, MiModal } from "@miplatform/components";
import {
  BUTTON_COLORS,
  BUTTON_VARIANTS,
  TYPOGRAPHY_VARIANTS,
} from "@miplatform/constants";
import { IUserListItemResponse } from "@miplatform/types";
import { getInitials, getRootUrl } from "@miplatform/utils";
import { mdiCalendarImportOutline } from "@mdi/js";
import {
  Box,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
  useTheme,
} from "@mui/material";

export type WorkOrderForcedToNextModalProps = {
  open: boolean;
  assignableUsers?: IUserListItemResponse[];
  isLoading?: boolean;
  onClose: () => void;
  onConfirm: (userId: string) => void;
};

export const WorkOrderForcedToNextModal = ({
  open,
  assignableUsers = [],
  isLoading = false,
  onClose,
  onConfirm,
}: WorkOrderForcedToNextModalProps) => {
  const { classes } = useStyles();
  const theme = useTheme();
  const [selectedUserId, setSelectedUserId] = useState<string>("");

  const handleClose = () => {
    setSelectedUserId("");
    onClose();
  };

  const handleConfirm = () => {
    if (!selectedUserId) {
      return;
    }
    onConfirm(selectedUserId);
  };

  useEffect(() => {
    if (!open) {
      setSelectedUserId("");
    }
  }, [open]);

  return (
    <MiModal
      open={open}
      closeModal={handleClose}
      width={448}
      titleIcon={mdiCalendarImportOutline}
      leftIconColor={theme.palette.primary.main}
      title="Force to Next"
      description="Select which user you would like to assign this work order to"
      loading={isLoading}
      actions={[
        {
          name: "Cancel",
          variant: BUTTON_VARIANTS.TEXT,
          color: BUTTON_COLORS.BLACK,
          onClick: handleClose,
        },
        {
          name: "Force to Next",
          variant: BUTTON_VARIANTS.CONTAINED,
          color: BUTTON_COLORS.PRIMARY,
          disabled: isLoading || !selectedUserId,
          onClick: handleConfirm,
        },
      ]}
    >
      <Box className={classes.root}>
        <RadioGroup
          value={selectedUserId}
          onChange={(event) => setSelectedUserId(event.target.value)}
          className={classes.userList}
        >
          {assignableUsers.map((user) => {
            const displayName =
              user.displayName ||
              user.name ||
              `${user.firstName ?? ""} ${user.lastName ?? ""}`.trim();
            const profilePictureRefId = user.profilePicture?.referenceId;
            const profilePictureUrl = profilePictureRefId
              ? `${getRootUrl()}/${profilePictureRefId}`
              : null;

            return (
              <FormControlLabel
                key={user.id}
                value={user.id}
                className={classes.userOption}
                control={<Radio size="small" />}
                label={
                  <Box className={classes.userLabel}>
                    <MiAvatar
                      className={classes.userAvatar}
                      src={profilePictureUrl}
                    >
                      {getInitials(displayName)}
                    </MiAvatar>
                    <Typography variant={TYPOGRAPHY_VARIANTS.SUBTITLE2}>
                      {displayName}
                    </Typography>
                  </Box>
                }
              />
            );
          })}
        </RadioGroup>
      </Box>
    </MiModal>
  );
};

const useStyles = makeStyles()((theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
  },
  userList: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
    maxHeight: 320,
    overflowY: "auto",
  },
  userOption: {
    margin: 0,
    padding: theme.spacing(1, 1.5),
    border: `1px solid ${theme.palette.grey.lightAccent}`,
    borderRadius: theme.spacing(1),
    "&:hover": {
      backgroundColor: theme.palette.grey.mediumLight,
    },
  },
  userLabel: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  userAvatar: {
    width: 24,
    height: 24,
    fontSize: theme.fontSize.xxsmall,
  },
}));
